import { useMutation } from '@apollo/client/react';
import {
  type CompleteStylistFragment,
  EndBreakAndCallNextDocument,
  EndServiceAndCallNextDocument,
  StartServiceDocument,
} from '@drycut/graphql';
import { Button, Spinner } from '@drycut/ui-components';
import { useBoolean } from 'usehooks-ts';

import { useStylistCardContext } from '../StylistCardContext';
import ManualPaymentReminder from './ManualPaymentReminder';

type Props = {
  stylist: CompleteStylistFragment;
};

export default function StartButton({ stylist }: Props): React.ReactElement {
  const { toggleActions, setActionLoading, actionLoading } = useStylistCardContext();
  const { value: manualPayModalOpen, toggle: toggleManualPayModal } = useBoolean(false);
  const { currentServiceExecution } = stylist;
  const manualBooking = currentServiceExecution?.booking?.manual || false;

  const [startService, { loading: starting }] = useMutation(StartServiceDocument, {
    variables: { input: { serviceExecutionId: currentServiceExecution?.id || '' } },
  });
  const [endServiceAndCallNext, { loading: endingService }] = useMutation(
    EndServiceAndCallNextDocument,
    { variables: { input: { serviceExecutionId: currentServiceExecution?.id || '' } } },
  );
  const [endBreakAndCallNext, { loading: endingBreak }] = useMutation(EndBreakAndCallNextDocument, {
    variables: { input: { stylistId: stylist.id } },
  });

  const loading = starting || endingService || endingBreak;

  // Returns the label for the button based on the stylist's current state.
  function buttonLabel() {
    if (stylist.state === 'WAITING_CUSTOMER') return 'Start';

    return 'Fertig';
  }

  function handleReminderClose() {
    toggleManualPayModal();
    nextCustomerFlow();
  }

  // Ends the current service and calls the next customer in line for this stylist, in a single
  // atomic backend mutation.
  async function nextCustomerFlow() {
    await endServiceAndCallNext();
    toggleActions();
  }

  async function handleStartOrNextCustomer(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setActionLoading(true);

    switch (stylist.state) {
      case 'BREAK': {
        await endBreakAndCallNext();
        toggleActions();
        break;
      }
      case 'WAITING_CUSTOMER': {
        await startService();
        toggleActions();
        break;
      }
      case 'WORKING': {
        if (manualBooking) {
          toggleManualPayModal();
        } else {
          nextCustomerFlow();
        }
        break;
      }
      default:
        // IDLE state doesn't allow to click the Next client/Start service button.
        break;
    }
    setActionLoading(false);
  }

  return (
    <>
      {/* The spinner is overlaid on the (hidden) label instead of using the Button `loading` prop,
          which renders it next to the label and makes the text wrap onto two lines, growing the
          button and shifting the layout while loading. */}
      <Button
        color="primary"
        disabled={stylist.state === 'IDLE' || actionLoading || loading}
        onClick={handleStartOrNextCustomer}
        className="text-base"
      >
        <span className="relative flex items-center justify-center">
          <span className={loading ? 'invisible' : undefined}>{buttonLabel()}</span>
          {loading && <Spinner className="absolute" />}
        </span>
      </Button>

      <ManualPaymentReminder open={manualPayModalOpen} onClose={handleReminderClose} />
    </>
  );
}
